import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Download } from 'lucide-react';
import { useState } from 'react';
import { AmountHeads, HeadCells, MonthPicker, Section } from '@/components/gst/return-parts';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import {
  downloadReturnJson,
  lastMonth,
  monthLabel,
  periodRange,
  stateLabel,
  useGstr3b,
  type GstHeads,
  type Gstr3bView,
} from '@/lib/gst';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useOrgLocale } from '@/lib/org-locale';

export const Route = createFileRoute('/taxes/gst/gstr3b')({
  validateSearch: (s: Record<string, unknown>): { period?: string } =>
    typeof s['period'] === 'string' && /^\d{4}-\d{2}$/.test(s['period'])
      ? { period: s['period'] }
      : {},
  component: Gstr3bPage,
});

/** Rows whose figures open the GST detail report filtered to them. */
const DRILL = new Set(['3.1a', '3.1b', '3.1c', '3.1d', '3.1e', '4A2', '4A3', '4A5', '4C']);

/** Screen T8 (document 06): GSTR-3B for a month, in form order, with the payment of tax. */
function Gstr3bPage() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const period = Route.useSearch().period ?? lastMonth();
  const r = useGstr3b(period);
  const [busy, setBusy] = useState(false);
  const { from, to } = periodRange(period);

  return (
    <>
      <PageHeader
        title="GSTR-3B"
        description="Summary return for the month: outward supplies, input tax credit and tax payable."
        actions={
          <>
            <MonthPicker
              period={period}
              onChange={(p) => void navigate({ to: '/taxes/gst/gstr3b', search: { period: p } })}
            />
            <Button asChild variant="outline">
              <Link
                to="/reports/$reportKey"
                params={{ reportKey: 'gstr-3b' }}
                search={{ from, to } as never}
              >
                Open as report
              </Link>
            </Button>
            <Button
              disabled={!r.data?.gstin || busy}
              title={r.data && !r.data.gstin ? 'Add the GSTIN in Company settings' : undefined}
              onClick={async () => {
                setBusy(true);
                try {
                  await downloadReturnJson(orgId!, 'gstr3b', period, r.data!.gstin!);
                } catch (e) {
                  toast((e as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Download className="size-4" /> Download JSON
            </Button>
          </>
        }
      />
      <p className="mb-4 text-sm">
        <Link to="/taxes/gst" className="text-primary hover:underline">
          GST
        </Link>{' '}
        › {monthLabel(period)}
        {r.data ? ` · due ${displayDate(r.data.dueDate)}` : ''}
      </p>
      {r.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : r.isError ? (
        <EmptyState title="Could not compute GSTR-3B" hint={r.error.message} />
      ) : (
        <Body r={r.data} from={from} to={to} />
      )}
    </>
  );
}

function Body({ r, from, to }: { r: Gstr3bView; from: string; to: string }) {
  const { currency } = useOrgLocale();
  const rows = (keys: string[]) => r.rows.filter((x) => keys.includes(x.key));
  const label = (key: string, text: string) =>
    DRILL.has(key) ? (
      <Link
        to="/reports/$reportKey"
        params={{ reportKey: 'gst-detail' }}
        search={{ from, to, types: key.toLowerCase() } as never}
        className="text-primary hover:underline"
      >
        ({key}) {text}
      </Link>
    ) : (
      <>
        ({key}) {text}
      </>
    );
  const table = (keys: string[], testId: string) => (
    <Table data-testid={testId}>
      <TableHeader>
        <TableRow>
          <TableHead>Nature of supplies</TableHead>
          <AmountHeads />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows(keys).map((x) => (
          <TableRow key={x.key} className={x.key === '4C' ? 'font-semibold' : undefined}>
            <TableCell>{label(x.key, x.label)}</TableCell>
            <HeadCells a={x} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  const s = r.setOff;
  const headRow = (text: string, h: GstHeads, bold = false) => (
    <TableRow className={bold ? 'font-semibold' : undefined}>
      <TableCell>{text}</TableCell>
      <HeadCells a={{ ...h, taxable: '0' }} taxable={false} />
    </TableRow>
  );
  const cash = [s.cash.igst, s.cash.cgst, s.cash.sgst, s.cash.cess].reduce(
    (t, v) => t + (toMinor(v) ?? 0n),
    0n,
  );

  return (
    <div className="space-y-6">
      {r.findings.length ? (
        <div
          className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 text-sm"
          role="note"
        >
          <p className="mb-1 inline-flex items-center gap-1.5 font-medium">
            <AlertTriangle className="size-4 text-[#B45309]" aria-hidden /> Exceptions
          </p>
          <ul className="list-disc pl-5">
            {r.findings.map((f, n) => (
              <li key={n}>{f.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <Section title="3.1 · Outward supplies and inward supplies liable to reverse charge">
        {table(['3.1a', '3.1b', '3.1c', '3.1d', '3.1e'], 'gstr3b-31')}
      </Section>
      <Section title="3.2 · Inter-state supplies to unregistered persons">
        {r.interState.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Place of supply</TableHead>
                <TableHead className="w-[150px] text-right">Taxable value</TableHead>
                <TableHead className="w-[150px] text-right">IGST</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.interState.map((x) => (
                <TableRow key={x.placeOfSupply}>
                  <TableCell>{stateLabel(x.placeOfSupply)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(x.taxable)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(x.igst)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="px-4 py-3 text-sm text-muted-foreground">None this month.</p>
        )}
      </Section>
      <Section title="4 · Eligible input tax credit">
        {table(['4A1', '4A2', '4A3', '4A4', '4A5', '4B1', '4B2', '4C', '4D1', '4D2'], 'gstr3b-4')}
        <p className="border-t px-4 py-2 text-xs text-muted-foreground">
          ITC reversals (4B) and ineligible ITC (4D) are not derived from the books; enter them on
          the portal. Match the input tax credit with GSTR-2B before filing.
        </p>
      </Section>
      <Section title="5 · Exempt, nil rated and non-GST inward supplies">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nature of supplies</TableHead>
              <TableHead className="w-[150px] text-right">Inter-state</TableHead>
              <TableHead className="w-[150px] text-right">Intra-state</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {r.exemptInward.map((x) => (
              <TableRow key={x.type}>
                <TableCell>
                  {x.type === 'GST'
                    ? 'Composition, exempt and nil rated supplies'
                    : 'Non-GST supplies'}
                </TableCell>
                <TableCell className="tnum text-right">{formatAmount(x.inter)}</TableCell>
                <TableCell className="tnum text-right">{formatAmount(x.intra)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section title="6.1 · Payment of tax" testId="gstr3b-payment">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <AmountHeads taxable={false} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {headRow('Tax payable (other than reverse charge)', s.liability)}
            {headRow('Paid through ITC: IGST', s.paidBy.igst)}
            {headRow('Paid through ITC: CGST', s.paidBy.cgst)}
            {headRow('Paid through ITC: SGST/UTGST', s.paidBy.sgst)}
            {headRow('Paid in cash (including reverse charge)', s.cash, true)}
            {headRow('ITC carried forward', s.carryForward)}
          </TableBody>
        </Table>
        <p className="border-t px-4 py-2 text-sm">
          Cash payable{' '}
          <span className="tnum font-semibold" data-testid="gstr3b-cash">
            {currency} {formatMinor(cash)}
          </span>
        </p>
      </Section>
    </div>
  );
}
